import { makeAutoObservable, runInAction, reaction } from 'mobx'
import { isEmail, logger } from 'shared/lib'
import { type IDropdownItem, showToast } from 'shared/ui'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { IUser } from 'entities/Users'
import { type IParamsUpdateInboxesEmail, type IResponseInbox } from 'entities/Inbox/api/types'
import { InboxesApi } from 'entities/Inbox/api/inboxes'
import { SendFromMultiStore } from 'widgets/SendFromMulti'
import type { ISendFromStoreItem } from 'widgets/SendFrom'

export type IEmailToSmsGroup = {
  email: string | null
  inboxStore: SendFromMultiStore
  selectedInboxes: Set<number>
  emailValue: string | null
  searchValue: string
}

export class EmailToSmsPageStore {
  private _isLoading = true
  private _isGroupsUpdating = false
  private _isPossibleToAddNewGroup = true
  private _isAllInboxesMapped = false
  private _usersByEmail: Map<string, IDropdownItem<IUser>> = new Map()
  private _usersById: Map<number, IDropdownItem<IUser>> = new Map()
  private _groups: Map<string | null, IEmailToSmsGroup> = new Map()
  private _allInboxes: Map<number, IResponseInbox> = new Map()
  private _selectedInboxesId: Set<number> = new Set()
  private _originalDropdownItemsMap: Map<number | string, IDropdownItem<ISendFromStoreItem>> =
    new Map()
  private _excludeShortcode = false

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    void this._init()

    reaction(
      () => this._entities.usersStore.membersList.length > 0,
      (isReady, _, r) => {
        if (isReady) {
          this._setUsers()
          r.dispose()
        }
      },
      { fireImmediately: true }
    )
  }

  private _init = async (isReinit?: boolean) => {
    const loader = isReinit ? '_isGroupsUpdating' : '_isLoading'

    try {
      runInAction(() => {
        this[loader] = true
      })

      if (!isReinit) await this._entities.usersStore.initPromise
      await Promise.all([this._fetchAllInboxes()])
      await this._initializeGroups()

      if (this.groups.length === 1 && !this.groups[0].emailValue) {
        runInAction(() => {
          this._isPossibleToAddNewGroup = false
        })
      }
    } catch (e) {
      runInAction(() => {
        this._isPossibleToAddNewGroup = false
      })

      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again',
      })

      logger.error(e)
    } finally {
      runInAction(() => {
        this[loader] = false
      })
    }
  }

  private _reinit() {
    void this._init(true)
  }

  private get _members(): IDropdownItem[] {
    return this._entities.usersStore.membersList.map((item: IUser) => ({
      id: item.id,
      label: item.name,
      value: item.name,
      data: item,
      searchPattern: `${item.name} ${item.email}`,
      avatarProps: {
        online: !item.isAway,
        size: 16,
        info: item.avatarInfo,
      },
    }))
  }

  private _fetchAllInboxes = async () => {
    try {
      const { data } = await this._entities.inboxesStore.getTeamsAll()

      data.forEach((item) => {
        if (this._excludeShortcode && item.number?.short_code) return

        runInAction(() => {
          this._allInboxes.set(item.id, item)
        })
      })
    } catch (e) {
      logger.error(e)
      throw e
    }
  }

  private _initializeGroups = async () => {
    await Promise.all(this._prepareGroups())
    this._setOriginalDropdownItemsMap()
    this.syncGroupsInboxes()
  }

  private _prepareGroups() {
    const initialGroups = new Map()
    this._groups = new Map()
    this._selectedInboxesId = new Set()

    this._allInboxes.forEach((inbox) => {
      const inboxes = initialGroups.get(inbox.email_to_sms) || []

      if (inbox.email_to_sms) this._selectedInboxesId.add(inbox.id)
      inboxes.push(inbox)
      initialGroups.set(inbox.email_to_sms, inboxes)
    })

    const sendFromMultiStores: Promise<unknown>[] = []
    initialGroups.forEach((inboxes: IResponseInbox[], email: string | null, map) => {
      if (!email && map.size !== 1) return

      const selectedIds = email === null ? [] : inboxes.map((inbox) => inbox.id)
      const store = this._createInboxStore()
      this._setGroupWithParams({
        email: email,
        store,
        selectedInboxes: selectedIds,
      })
      sendFromMultiStores.push(store.initPromise)
    })

    this._setInitialStates(!initialGroups.get(null)?.length)

    return sendFromMultiStores
  }

  private _setInitialStates(hasUnselectedActiveOptions: boolean) {
    this._isAllInboxesMapped = hasUnselectedActiveOptions
    this._isPossibleToAddNewGroup = !hasUnselectedActiveOptions
  }

  private _setOriginalDropdownItemsMap() {
    const firstGroup = this.groups[0]
    if (firstGroup) {
      const allItemsMap = firstGroup.inboxStore['_itemsMap']

      if (!this._originalDropdownItemsMap.size) {
        this._originalDropdownItemsMap = new Map(allItemsMap)
      }
    }
  }

  private _setUsers() {
    this.members.forEach((member) => {
      if (member.data) {
        this._usersByEmail.set(member.data.email, member)
        this._usersById.set(member.data.id, member)
      }
    })
  }

  private _createNewGroup = async () => {
    if (this._groups.get('')) return

    const store = this._createInboxStore()
    this._setGroupWithParams({
      email: null,
      store,
      selectedInboxes: [],
    })
    await store.initPromise
    this.syncGroupsInboxes()
  }

  private _createInboxStore() {
    return new SendFromMultiStore(this._entities, {
      getDropdownItemsOptions: {
        excludeShortCode: this._excludeShortcode,
        hideChildren: true,
        disabledClosedTooltip: true,
        excludeSendingOptions: true,
      },
    })
  }

  private _setGroupWithParams(params: {
    email: string | null
    store: SendFromMultiStore
    selectedInboxes: number[]
  }) {
    this._groups.set(params.email || '', {
      email: params.email || '',
      inboxStore: params.store,
      selectedInboxes: new Set(params.selectedInboxes),
      emailValue: params.email,
      searchValue: '',
    })
  }

  syncGroupsInboxes() {
    this._groups.forEach((group) => {
      const selectedIds = Array.from(group.selectedInboxes)
      const selectedIdsSet = new Set(selectedIds)
      const allowedIds = new Set(this.getAllowedInboxesIdsInGroup(selectedIds))
      const dropdownItemsAll = new Map(this._originalDropdownItemsMap)
      const allowedInboxes = Array.from(dropdownItemsAll.values()).filter((inbox) => {
        return allowedIds.has(+inbox.id)
      })

      group.inboxStore.setDropdownItems(allowedInboxes)

      const selectedInboxes = allowedInboxes.filter((inbox) => selectedIdsSet.has(+inbox.id))
      selectedInboxes.forEach((inbox) => {
        group.inboxStore.setSelectedDropdownItem(inbox)
      })
    })
  }

  get isLoading() {
    return this._isLoading
  }

  get isGroupsUpdating() {
    return this._isGroupsUpdating
  }

  get groups() {
    return Array.from(this._groups.values())
  }

  get members(): IDropdownItem<IUser>[] {
    return this._members
  }

  get isAllInboxesMapped() {
    return this._isAllInboxesMapped
  }

  get isPossibleToAddNewGroup() {
    return this._isPossibleToAddNewGroup
  }

  setGroupSearchValue(group: IEmailToSmsGroup, value: string) {
    group.searchValue = value
  }

  isValidEmail(email: string) {
    return isEmail(email)
  }

  addNewGroup = () => {
    if (!this._isPossibleToAddNewGroup) return

    this._isPossibleToAddNewGroup = false
    void this._createNewGroup()
  }

  userInfo = (email: string) => {
    if (!email) return null

    return this._usersByEmail.get(email)
  }

  getAllowedInboxesIdsInGroup(selectedIdsInGroup: number[]) {
    const allIds = [...this._originalDropdownItemsMap.keys()]
    const allSelectedIds = new Set(this._selectedInboxesId)
    const freeIds = allIds.filter((el) => !allSelectedIds.has(+el))

    return [...selectedIdsInGroup, ...freeIds]
  }

  sendInboxes = async (params: IParamsUpdateInboxesEmail) => {
    try {
      runInAction(() => {
        this._isGroupsUpdating = true
      })

      await InboxesApi.updateInboxesEmail(params)
    } catch (e) {
      logger.error(e)

      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again',
      })
    } finally {
      runInAction(() => {
        this._isGroupsUpdating = false
      })

      this._reinit()
    }
  }

  prepareSendingData = async (updatedInboxes: {
    email: string
    addedInboxes: number[]
    removedInboxes: number[]
  }) => {
    const params: IParamsUpdateInboxesEmail = {
      items: [],
      sms_message_type: 'body',
    }

    if (updatedInboxes.addedInboxes.length && updatedInboxes.email) {
      params.items.push({
        email: updatedInboxes.email,
        team_ids: updatedInboxes.addedInboxes,
      })
    }

    if (updatedInboxes.removedInboxes.length) {
      params.items.push({
        email: '',
        team_ids: updatedInboxes.removedInboxes,
      })
    }

    await this.sendInboxes(params)
  }

  updateEmail = (group: IEmailToSmsGroup, userId?: number) => {
    const newValue = userId ? this._usersById.get(userId)?.data?.email : group.searchValue

    if (group.emailValue === newValue || !newValue) return
    group.emailValue = newValue

    const added = group.inboxStore.selectedInboxIds
    if (added.length) {
      void this.prepareSendingData({
        email: newValue,
        addedInboxes: added,
        removedInboxes: [],
      })
    }
  }

  updateInboxes = (group: IEmailToSmsGroup, isOpen: boolean) => {
    if (isOpen || !group.emailValue) return

    const initialSelected = group.selectedInboxes
    const currentSelected = new Set(group.inboxStore.selectedInboxIds)
    const added = [...currentSelected].filter((id: number) => !initialSelected.has(id))
    const removed = [...initialSelected].filter((id: number) => !currentSelected.has(id))

    if (added.length || removed.length) {
      void this.prepareSendingData({
        email: group.emailValue,
        addedInboxes: added,
        removedInboxes: removed,
      })
    }
  }

  deleteGroup = async (group: IEmailToSmsGroup) => {
    if (group.emailValue) {
      await this.prepareSendingData({
        email: group.emailValue,
        addedInboxes: [],
        removedInboxes: group.inboxStore.selectedInboxIds,
      })
    }
  }

  removeEmptyGroup = () => {
    this._groups.delete('')
    this._isPossibleToAddNewGroup = true
  }
}
